import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { addDays, isWeekend, format } from 'date-fns';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Header from "../../../components/Header";

const generateWeekDays = (startDate) => {
  const days = [];

  for (let i = 0; i < 7; i++) {
    const currentDate = addDays(startDate, i);
    if (!isWeekend(currentDate)) {
      days.push({
        date: currentDate,
        isWeekend: isWeekend(currentDate),
      });
    }
  }

  return days;
};

const feriados = [
  '01-01', // Ano Novo
  '04-25', // Dia da Liberdade
  '05-01', // Dia do Trabalhador
  '06-10', // Dia de Portugal
  '08-15', // Assunção de Nossa Senhora
  '10-05', // Implantação da República
  '11-01', // Dia de Todos os Santos
  '12-01', // Restauração da Independência
  '12-08', // Imaculada Conceição
  '12-25'  // Natal
];

const isHoliday = (date) => {
  const formattedDate = format(date, 'MM-dd');
  return feriados.includes(formattedDate);
};

const EditHoras = () => {
  const { id } = useParams();
  const [dadosHoras, setDadosHoras] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [totalHours, setTotalHours] = useState(0);
  const [clientes, setClientes] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [aprovadores, setAprovadores] = useState({ aprovador1: '', aprovador2: '' });
  const [aprovador1Id, setAprovador1Id] = useState('');
  const [aprovador2Id, setAprovador2Id] = useState('');
  const [weekDays, setWeekDays] = useState(generateWeekDays(new Date()));
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [dataFinal, setDataFinal] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [horasResponse, clientesResponse, colaboradoresResponse] = await Promise.all([
        axios.get(`https://pf-auditores.ew.r.appspot.com/api/horas-trabalhadas/${id}`),
        axios.get('https://pf-auditores.ew.r.appspot.com/api/clientes'),
        axios.get('https://pf-auditores.ew.r.appspot.com/api/colaboradores')
      ]);

      const formattedHorasResponse = {
        ...horasResponse.data,
        dia1Data: format(new Date(horasResponse.data.dia1Data), 'dd/MM/yyyy'),       
      };
      const dataFinal = formattedHorasResponse.dia1Data;

      setDadosHoras(formattedHorasResponse);
      setTotalHours(horasResponse.data.totalHoras);
      setClientes(clientesResponse.data);
      setColaboradores(colaboradoresResponse.data);
      setDataFinal(horasResponse.data.dia1Data);
      setIsLoading(false);

      if (horasResponse.data.clienteId) {
        const response = await axios.get(`https://pf-auditores.ew.r.appspot.com/api/clientes/${horasResponse.data.clienteId}`);
        const { aprovador1, aprovador2 } = response.data;
        setAprovador1Id(aprovador1);
        setAprovador2Id(aprovador2);

        const aprovadoresData = {};

        if (aprovador1) {
          const aprovador1Response = await axios.get(`https://pf-auditores.ew.r.appspot.com/api/colaboradores/${aprovador1}`);
          aprovadoresData.aprovador1 = getFirstAndLastName(aprovador1Response.data.nome);
        }
        if (aprovador2) {
          const aprovador2Response = await axios.get(`https://pf-auditores.ew.r.appspot.com/api/colaboradores/${aprovador2}`);
          aprovadoresData.aprovador2 = getFirstAndLastName(aprovador2Response.data.nome);
        }

        setAprovadores(aprovadoresData);
        setSelectedClientName(response.data.nomeCliente);
      }

      if (horasResponse.data.semana) {
        const mappedWeekDays = horasResponse.data.semana.map(day => ({
          date: new Date(day.date),
          horas: day.horas
        }));
        setWeekDays(mappedWeekDays);
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      setFormSubmitting(true);
      
      const requestData = {
        clienteId: values.clienteId,
        aprovador1: values.aprovador1,
        aprovador2: values.aprovador2,
        colaboradorId: values.colaboradorId,
        estado: values.estado,
        dia1Data: format(new Date(values.dia1Data), 'yyyy-MM-dd'),
        descricao: values.descricao,
        totalHoras: values.totalHoras,
      };
      console.log(JSON.stringify(requestData));
      await axios.put(`https://pf-auditores.ew.r.appspot.com/api/horas-trabalhadas/${id}`, requestData);
  
      navigate('/horas');
    } catch (error) {
      console.error('Erro ao atualizar horas:', error);
      alert('Ocorreu um erro ao atualizar as horas trabalhadas. Por favor, tente novamente mais tarde.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const getFirstAndLastName = (fullName) => {
    const names = fullName.split(' ');
    if (names.length > 1) {
      return `${names[0]} ${names[names.length - 1]}`;
    }
    return fullName;
  };

  if (isLoading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
  <Header title="Editar Horas Semanais" subtitle="Editar horas trabalhadas diariamente" />
  {dadosHoras && (
    <Formik
      enableReinitialize
      initialValues={{
        clienteId: dadosHoras.clienteId || '',
        aprovador1: aprovador1Id || '',
        aprovador2: aprovador2Id || '',
        colaboradorId: dadosHoras.colaboradorId || '',
        estado: dadosHoras.estado || 'Pendente',
        totalHoras: dadosHoras.totalHoras,
        dia1Data: dataFinal,
        descricao: dadosHoras.descricao,
      }}
      validationSchema={yup.object().shape({
        clienteId: yup.string().required('Cliente é obrigatório'),
        aprovador1: yup.string().required('Aprovador 1 é obrigatório'),
        aprovador2: yup.string().required('Aprovador 2 é obrigatório'),
        descricao: yup.string().required('Descrição obrigatória').min(0, 'Descrição inválida'),
        totalHoras: yup.string().required('Total de horas é obrigatório').min(0, 'Total de horas inválido'),
        dia1Data: yup.string().required('Data inválida'),
        colaboradorId: yup.string().required('Colaborador é obrigatório'),
        estado: yup.string().required('Estado é obrigatório'),
      })}
      onSubmit={handleFormSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit}>
          <Box display="flex" justifyContent="space-between" mb="20px" gap="20px">
            <TextField
              variant="filled"
              fullWidth
              label="Cliente"
              value={selectedClientName}
              disabled
            />
          </Box>
          <Box display="flex" justifyContent="space-between" mb="20px" gap="20px">
            <FormControl variant="filled" fullWidth>
              <InputLabel id="aprovador1-label">Aprovador 1</InputLabel>
              <Select
                labelId="aprovador1-label"
                name="aprovador1"
                value={values.aprovador1}
                onBlur={handleBlur}
                onChange={handleChange}
                error={Boolean(touched.aprovador1 && errors.aprovador1)}
                helperText={touched.aprovador1 && errors.aprovador1}
                disabled
              >
                {colaboradores.map((colaborador) => (
                  <MenuItem key={colaborador.id} value={colaborador.id}>
                    {colaborador.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="filled" fullWidth>
              <InputLabel id="aprovador2-label">Aprovador 2</InputLabel>
              <Select
                labelId="aprovador2-label"
                name="aprovador2"
                value={values.aprovador2}
                onBlur={handleBlur}
                onChange={handleChange}
                error={Boolean(touched.aprovador2 && errors.aprovador2)}
                helperText={touched.aprovador2 && errors.aprovador2}
                disabled
              >
                {colaboradores.map((colaborador) => (
                  <MenuItem key={colaborador.id} value={colaborador.id}>
                    {colaborador.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box display="flex" justifyContent="space-between" mb="20px" gap="20px">
            <TextField
              fullWidth
              variant="filled"
              type="text"
              label="Descrição"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.descricao}
              name="descricao"
              error={!!touched.descricao && !!errors.descricao}
              helperText={touched.descricao && errors.descricao}
              disabled
            />
          </Box>
          <Box display="flex" justifyContent="space-between" mb="20px" gap="20px">
            <TextField
              fullWidth
              variant="filled"
              type="text"
              label="Data"
              onBlur={handleBlur}
              onChange={handleChange}
              value={format(new Date(values.dia1Data), 'dd/MM/yyyy')}
              name="dia1Data"
              error={!!touched.dia1Data && !!errors.dia1Data}
              helperText={touched.dia1Data && errors.dia1Data}
              disabled
            />
            <TextField
              fullWidth
              variant="filled"
              type="text"
              label="Total de Horas"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.totalHoras}
              name="totalHoras"
              error={!!touched.totalHoras && !!errors.totalHoras}
              helperText={touched.totalHoras && errors.totalHoras}
              disabled
            />
            <FormControl variant="filled" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="estado"
                value={values.estado}
                onBlur={handleBlur}
                onChange={handleChange}
                error={!!touched.estado && !!errors.estado}
              >
                <MenuItem value="Pendente">Pendente</MenuItem>
                <MenuItem value="Aprovado">Aprovado</MenuItem>
                <MenuItem value="Rejeitado">Rejeitado</MenuItem>
              </Select>
              {touched.estado && errors.estado && (
                <div style={{ color: 'red', marginTop: 8 }}>{errors.estado}</div>
              )}
            </FormControl>
          </Box>
          <Box display="flex" justifyContent="flex-end" mt="20px" gap="20px">
            <Button
              type="submit"
              color="secondary"
              variant="contained"
              disabled={formSubmitting}
            >
              {formSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Atualizar'}
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  )}
</Box>

  );
};

export default EditHoras;
